import {ActionIcon, MantineProvider, Menu} from '@mantine/core'
import {notifications} from '@mantine/notifications'
import {IconDownload} from '@tabler/icons-react'
import {toPng} from 'html-to-image'
import {type ReactNode, useCallback, useState} from 'react'
import {createRoot, type Root} from 'react-dom/client'

import {cssVariablesResolver, theme} from 'src/theme'

const DEFAULT_EXPORT_WIDTH_BY_KIND = {
    chart: 816,
    table: 680,
} as const
const EXPORT_PIXEL_RATIO = 3
const OFFSCREEN_BG = '#F7F6FA'
const OFFSCREEN_PADDING_PX = 16

export type ExportKind = 'chart' | 'table'

const DEFAULT_KINDS: readonly ExportKind[] = ['chart', 'table']

export type ExportImageButtonProps = {
    title: string
    renderContent: (args: {kind: ExportKind}) => ReactNode
    filenameBase?: string
    /** Which export kinds to expose in the menu. Defaults to chart and table. */
    kinds?: readonly ExportKind[]
    /** Base width (CSS px) for the offscreen capture root per kind; omitted kinds use {@link DEFAULT_EXPORT_WIDTH_BY_KIND}. */
    exportWidthPx?: Partial<Record<ExportKind, number>>
    captureFn?: typeof toPng<HTMLElement>
}

function slugifyFilenameBase(text: string): string {
    return text
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-_]/g, '')
        .replace(/-+/g, '-')
}

function triggerDownload(dataUrl: string, filename: string) {
    const anchor = document.createElement('a')
    anchor.href = dataUrl
    anchor.download = filename
    anchor.rel = 'noopener'
    anchor.click()
}

/**
 * Creates the fixed-position root element used for html-to-image capture (see comments inline).
 */
function createOffscreenExportRoot(widthPx: number): HTMLDivElement {
    const container = document.createElement('div')
    container.setAttribute('data-export-root', 'true')
    Object.assign(container.style, {
        // Rendering content off-screen doesn't seem to work in Chrome. Composer 2 says it's
        // because the positioning throws off Chrome's rasterizer. In its own words:
        //
        // Chromium + html-to-image: far-off `left` yields empty PNGs; non-zero `left` or
        // `top` on this fixed root skews/clips foreignObject → canvas. Keep origin at (0,0).
        // Negative z-index stacks the duplicate behind the app (no flash) but still paints
        // for capture—unlike opacity (would dim the clone). `pointer-events: none` avoids
        // stealing input. Requires normal page content to paint above (typical z-index:0).
        //
        // So instead, we render the content in a fixed position and render it below
        // everything else via z-index.
        position: 'fixed',
        left: '0',
        top: '0',
        width: `${widthPx}px`,
        boxSizing: 'border-box',
        backgroundColor: OFFSCREEN_BG,
        padding: `${OFFSCREEN_PADDING_PX}px`,
        pointerEvents: 'none',
        zIndex: '10000',
    } satisfies Partial<CSSStyleDeclaration>)
    return container
}

/**
 * Renders analytics content off-screen and downloads it as a PNG.
 */
export function ExportImageButton({
    title,
    renderContent,
    filenameBase,
    kinds = DEFAULT_KINDS,
    exportWidthPx,
    captureFn = toPng,
}: ExportImageButtonProps) {
    const [busy, setBusy] = useState(false)

    const runExport = useCallback(
        async (kind: ExportKind) => {
            const widthPx = exportWidthPx?.[kind] ?? DEFAULT_EXPORT_WIDTH_BY_KIND[kind]
            const base = slugifyFilenameBase(filenameBase ?? title) || 'export'
            const filename = kind === 'chart' ? `${base}-chart.png` : `${base}-table.png`

            const container = createOffscreenExportRoot(widthPx)
            document.body.appendChild(container)

            let root: Root | undefined
            try {
                setBusy(true)
                root = createRoot(container)
                root.render(
                    <MantineProvider theme={theme} cssVariablesResolver={cssVariablesResolver}>
                        {renderContent({kind})}
                    </MantineProvider>,
                )

                if (document.fonts?.ready) {
                    await document.fonts.ready.catch(() => {})
                }

                // Give it a few frames to ensure charts are rendered. Two frames wasn't quite
                // consistent enough.
                await new Promise<void>((resolve) => {
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            requestAnimationFrame(() => {
                                resolve(undefined)
                            })
                        })
                    })
                })

                const measuredW = Math.ceil(container.scrollWidth)
                const measuredH = Math.max(
                    Math.ceil(container.scrollHeight),
                    Math.ceil(container.offsetHeight),
                )
                const exportWidth = measuredW > 0 ? measuredW : widthPx
                const exportHeight = Math.max(measuredH, 1)

                const dataUrl = await captureFn(container, {
                    pixelRatio: EXPORT_PIXEL_RATIO,
                    // Avoid html-to-image bug: normalizeFontFamily(undefined) when embedding @font-face.
                    // Fonts are already loaded in the page for the offscreen subtree.
                    skipFonts: true,
                    width: exportWidth,
                    height: exportHeight,
                    backgroundColor: OFFSCREEN_BG,
                    // Root must not stay `position: fixed` when cloned into SVG foreignObject.
                    style: {
                        position: 'relative',
                        left: '0',
                        top: '0',
                        zIndex: 'auto',
                        overflow: 'visible',
                    },
                })
                triggerDownload(dataUrl, filename)
            } catch (err) {
                console.error('Export image failed:', err)
                notifications.show({
                    title: 'Export failed',
                    message: err instanceof Error ? err.message : 'Could not create image.',
                    color: 'red',
                })
            } finally {
                root?.unmount()
                container.remove()
                setBusy(false)
            }
        },
        [captureFn, exportWidthPx, filenameBase, renderContent, title],
    )

    const includeChart = kinds.includes('chart')
    const includeTable = kinds.includes('table')
    const showChartSectionLabel = includeChart && includeTable

    return (
        <Menu position="bottom-end" withinPortal>
            <Menu.Target>
                <ActionIcon
                    variant="light"
                    color="brand"
                    size="lg"
                    aria-label="Download view as image"
                    loading={busy}
                    disabled={busy}
                >
                    <IconDownload size={20} />
                </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
                {includeChart && (
                    <>
                        {showChartSectionLabel && <Menu.Label>Image Type</Menu.Label>}
                        <Menu.Item onClick={() => void runExport('chart')}>Chart</Menu.Item>
                    </>
                )}
                {includeTable && (
                    <Menu.Item onClick={() => void runExport('table')}>Table</Menu.Item>
                )}
            </Menu.Dropdown>
        </Menu>
    )
}
